import React, { useState, useMemo, useCallback } from "react"
import { isEmpty, map, filter, get, chain, toLower, remove } from "lodash"
import { TextField, Tabs, Tab, List, ListItemText, ListItemAvatar, Divider, ListItemButton, InputAdornment } from "@mui/material"
import { Search } from "@mui/icons-material"
import type { TextFieldVariants } from "@mui/material"
import { createMatch } from "helpers/string"
import { camelCaseToSpace } from "./helper"
import { DEFAULT_TAB_ORDER } from "./constants"
import { ItemIcon } from "./item-icon"
import "./styles.scss"
import type { FactorRule } from "./types"

type ActionCallback = () => void

interface SearchFactorProps {
  selectItem: (item: FactorRule) => void
  applyAction?: (callback: ActionCallback) => () => void
  factors: FactorRule[]
  factorValueLabel?: string
  multiSelect?: boolean
  tabOrderOverride?: Record<string, number>
  variant?: TextFieldVariants
}

export default function SearchFactor({
  selectItem,
  applyAction,
  factors,
  factorValueLabel,
  multiSelect,
  tabOrderOverride,
  variant = "standard",
}: SearchFactorProps) {
  const [searchValue, setSearch] = useState("")
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [selectedTab, setSelectedTab] = useState("all")

  const tabOrder: Record<string, number> = useMemo(() => {
    return tabOrderOverride || DEFAULT_TAB_ORDER
  }, [tabOrderOverride])

  function getItemType({ type, classificationTier, contentSetId, category }: FactorRule) {
    return classificationTier || contentSetId || category || type
  }

  const searchResults = useMemo(() => {
    const match = createMatch(searchValue)
    const results = filter(factors, factor => match(get(factor, "name")))

    const tabs = [
      { id: "all", count: results.length, disabled: results.length === 0 },
      ...chain(results)
        .groupBy(getItemType)
        .map((value, key) => ({
          id: key,
          count: value.length,
          disabled: value.length === 0,
          sort: get(tabOrder, toLower(key)),
        }))
        .sortBy("sort")
        .value(),
    ]

    const notInSelectedTab = (element: FactorRule) => selectedTab !== "all" && getItemType(element) !== selectedTab
    remove(results, notInSelectedTab)

    return {
      tabs,
      items: results,
    }
  }, [factors, searchValue, selectedTab, tabOrder])

  function getResultKey({ type, id, classificationTier, classificationId }: FactorRule & { classificationId?: string }) {
    const classificationKey = classificationTier || classificationId
    return `${type}-${classificationKey || type}-${id}`
  }

  const handleSelectItem = useCallback((item: FactorRule) => {
    if (applyAction && !multiSelect) {
      applyAction(() => selectItem(item))()
    } else {
      selectItem(item)
    }
  }, [applyAction, multiSelect, selectItem])

  const handleKeyPress = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowDown":
        setSelectedIndex(selectedIndex + 1)
        break
      case "ArrowUp":
        setSelectedIndex(selectedIndex - 1)
        break
      case "Enter": {
        const { items } = searchResults
        if (selectedIndex >= 0 && items.length > selectedIndex) {
          handleSelectItem(items[selectedIndex])
        }
        break
      }
    }
  }, [selectedIndex, searchResults, handleSelectItem])

  const noItems = searchResults.items.length === 0

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setSearch(value)
  }, [setSearch])

  const { tabs, items } = searchResults

  return (
    <div className="components_search-factors modules_unified-search">
      <TextField
        variant={variant}
        tabIndex={0}
        onKeyDown={handleKeyPress}
        placeholder="Search…"
        value={searchValue}
        autoFocus
        onChange={onChange}
        slotProps={{
        root: {
          className: "search-input-wrapper",
        },
        htmlInput: {
          className: "search-input",
        },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        },
      }}
      />
    {(!isEmpty(tabs) && !noItems) && <Tabs
      scrollButtons="auto"
      variant="scrollable"
      className="tabs"
      value={selectedTab}
      indicatorColor="primary"
      textColor="primary"
      onChange={(_event: React.SyntheticEvent, value: string) => setSelectedTab(value)}
    >
      {map(tabs, ({ id, count, disabled }) => (<Tab key={id} value={id} label={`${camelCaseToSpace(id)} (${count})`} disabled={disabled} />))}
    </Tabs>}
    {!noItems
      && <div className="search-box">
        {factorValueLabel && <div className="sub-header"><span>{factorValueLabel}</span></div>}
        <List component="nav">
          {map(items, (item, index) =>
            <ListItemButton
              tabIndex={-1}
              key={getResultKey(item)}
              disabled={item.disabled}
              onClick={() => { handleSelectItem(item) }}
              selected={index === selectedIndex}
            >
              <ListItemAvatar>
                <ItemIcon item={item} />
              </ListItemAvatar>
              <ListItemText
                primary={item.name}
                secondary={item.info}
              />

              {factorValueLabel && item.value && <span>{item.value}</span>}
            </ListItemButton>,
          )}
        </List>
        <Divider />
      </div>}
    {searchValue && noItems
      && <div className="search-box">
        <div className="search-empty">No factors matching your search</div>
      </div>}
    </div>
  )
}
